// layout-switcher.jsx — the "Layout · {name}" pill (bottom-right) + the
// 4-card bottom sheet that lets the visitor pick a layout. Choice persists
// per device via localStorage. On first paint per session the pill pulses
// 3× so visitors notice they can change layouts.

import { useEffect, useState } from 'preact/hooks';
import { BottomSheet } from './atoms.jsx';
import { readHeroPref, setHeroPref } from './hero/media-policy.js';
import { SECTIONS, isSectionEnabled, setSectionEnabled } from './section-prefs.js';
import { setUnit, getRawState } from './data-bridge.js';
import { VariantA } from './layouts/variant-a.jsx';
import { VariantB } from './layouts/variant-b.jsx';
import { VariantC } from './layouts/variant-c.jsx';
import { VariantD } from './layouts/variant-d.jsx';

export const LAYOUTS = [
  { id: 'D', label: 'Editorial',  blurb: 'Hero-led, photographic, long-form sections',  Comp: VariantD },
  { id: 'B', label: 'Cards',      blurb: 'Paged favorites, stacked rounded cards',      Comp: VariantB },
  { id: 'C', label: 'App shell',  blurb: 'Bottom-tabs — Now · Hourly · 10-day · Radar', Comp: VariantC },
  { id: 'A', label: 'Classic',    blurb: 'Mirrors the existing site, mobile-first',     Comp: VariantA },
];

const byId = Object.fromEntries(LAYOUTS.map((l) => [l.id, l]));
const PULSE_KEY = 'weatherfcc:v32826:layout-pill-pulsed';

export function LayoutPill({ brand, layoutId, onOpen }) {
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem(PULSE_KEY) !== '1') {
        setPulse(true);
        sessionStorage.setItem(PULSE_KEY, '1');
        const id = setTimeout(() => setPulse(false), 7500); // 3 cycles × 2.4s + a buffer
        return () => clearTimeout(id);
      }
    } catch (_) {}
  }, []);

  const label = byId[layoutId]?.label || 'Editorial';
  // Variant C has a 96px-tall bottom tab bar; lift the pill above it.
  const bottomOffset = layoutId === 'C' ? 96 : 32;

  return (
    <div style={{ position: 'absolute', right: 14, bottom: bottomOffset, zIndex: 60 }}>
      <button onClick={onOpen} className="wx-tap" aria-label={`Change layout (currently ${label})`} style={{
        position: 'relative', display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
        borderRadius: 999, background: `${brand.panel}d8`, color: brand.text,
        border: `1px solid ${brand.line}`,
        backdropFilter: 'blur(14px) saturate(140%)', WebkitBackdropFilter: 'blur(14px) saturate(140%)',
        boxShadow: '0 6px 18px rgba(0,0,0,.4)', cursor: 'pointer', fontSize: 12,
        fontFamily: brand.eyebrowFont, fontWeight: 600,
      }}>
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" fill="none" stroke={brand.accent} strokeWidth="1.6" strokeLinecap="round">
          <rect x="1.5" y="1.5" width="4" height="4" rx="1"/><rect x="8.5" y="1.5" width="4" height="4" rx="1"/>
          <rect x="1.5" y="8.5" width="4" height="4" rx="1"/><rect x="8.5" y="8.5" width="4" height="4" rx="1"/>
        </svg>
        Layout · {label}
        {pulse && (
          <span aria-hidden="true" style={{
            position: 'absolute', inset: -4, borderRadius: 999,
            border: `2px solid ${brand.accent}`,
            animation: 'wx-pulse 2.4s ease-out 1.2s 3 forwards',
            pointerEvents: 'none',
          }}/>
        )}
      </button>
    </div>
  );
}

export function LayoutSheet({ open, onClose, brand, current, onChoose, densityId, onChooseDensity }) {
  const [heroPref, setHeroPrefLocal] = useState(readHeroPref());
  return (
    <BottomSheet open={open} onClose={onClose} title="Display" brand={brand} height="78%">
      <div style={{ padding: '4px 16px 20px' }}>
        <div style={{ fontSize: 13, color: brand.textDim, lineHeight: 1.5, marginBottom: 14 }}>
          Same forecast, four ways to read it. Pick what feels right — you can change it anytime.
        </div>
        {LAYOUTS.map((l) => {
          const active = l.id === current;
          return (
            <button key={l.id} onClick={() => { onChoose(l.id); onClose(); }} className="wx-tap" style={{
              width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 14,
              padding: '12px 14px', borderRadius: 14, marginBottom: 8,
              background: active ? `${brand.accent}1a` : brand.panelHi,
              border: `1px solid ${active ? brand.accent : brand.line}`,
              color: brand.text, cursor: 'pointer',
            }}>
              <LayoutGlyph id={l.id} brand={brand} active={active}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: brand.text }}>{l.label}</div>
                <div style={{ fontSize: 12, color: brand.textDim, marginTop: 2, lineHeight: 1.4 }}>{l.blurb}</div>
              </div>
              {active && (
                <div style={{
                  width: 22, height: 22, borderRadius: 11, background: brand.accent,
                  color: brand.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, flexShrink: 0,
                }}>✓</div>
              )}
            </button>
          );
        })}

        {/* Density */}
        <div style={{ marginTop: 18, fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em' }}>Density</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          {[
            { id: 'comfortable', label: 'Comfy' },
            { id: 'compact',     label: 'Compact' },
          ].map((d) => {
            const active = d.id === densityId;
            return (
              <button key={d.id} onClick={() => onChooseDensity(d.id)} className="wx-tap" style={{
                flex: 1, padding: '10px 12px', borderRadius: 10,
                background: active ? brand.accent : 'transparent',
                color: active ? brand.bg : brand.text,
                border: `1px solid ${active ? brand.accent : brand.line}`,
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>{d.label}</button>
            );
          })}
        </div>

        {/* Units */}
        <div style={{ marginTop: 18, fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em' }}>Units</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          {[
            { id: 'F', label: '°F' },
            { id: 'C', label: '°C' },
          ].map((u) => {
            const active = getRawState().unit === u.id;
            return (
              <button key={u.id} onClick={() => setUnit(u.id)} className="wx-tap" style={{
                flex: 1, padding: '10px 12px', borderRadius: 10,
                background: active ? brand.accent : 'transparent',
                color: active ? brand.bg : brand.text,
                border: `1px solid ${active ? brand.accent : brand.line}`,
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>{u.label}</button>
            );
          })}
        </div>

        {/* Customize sections */}
        <SectionsCustomizer brand={brand}/>

        {/* Hero motion */}
        <div style={{ marginTop: 18, fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em' }}>Hero motion</div>
        <div style={{ fontSize: 11, color: brand.textDim, marginTop: 4, lineHeight: 1.4 }}>
          Save-Data and reduced-motion always disable video.
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          {[
            { id: 'auto',   label: 'Auto' },
            { id: 'video',  label: 'Always on' },
            { id: 'poster', label: 'Always off' },
          ].map((opt) => {
            const active = opt.id === heroPref;
            return (
              <button key={opt.id} onClick={() => { setHeroPref(opt.id); setHeroPrefLocal(opt.id); }} className="wx-tap" style={{
                flex: 1, padding: '10px 8px', borderRadius: 10,
                background: active ? brand.accent : 'transparent',
                color: active ? brand.bg : brand.text,
                border: `1px solid ${active ? brand.accent : brand.line}`,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>{opt.label}</button>
            );
          })}
        </div>
      </div>
    </BottomSheet>
  );
}

function SectionsCustomizer({ brand }) {
  const [open, setOpen] = useState(false);
  const [, tick] = useState(0);
  return (
    <>
      <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em' }}>Sections</span>
        <button onClick={() => setOpen((v) => !v)} style={{
          background: 'transparent', border: 'none', color: brand.accent, fontSize: 11,
          fontFamily: brand.eyebrowFont, fontWeight: 600, cursor: 'pointer', padding: 0,
          textTransform: 'uppercase', letterSpacing: '.06em',
        }}>{open ? 'Done' : 'Customize'}</button>
      </div>
      {open && (
        <div style={{ marginTop: 8, padding: '8px 10px', background: brand.panelHi, border: `1px solid ${brand.line}`, borderRadius: 10 }}>
          {SECTIONS.map((s) => {
            const enabled = isSectionEnabled(s.id);
            return (
              <label key={s.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px',
                borderBottom: `1px dashed ${brand.line}`, cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={() => { setSectionEnabled(s.id, !enabled); tick((n) => n + 1); }}
                  style={{ accentColor: brand.accent, width: 16, height: 16, margin: 0 }}
                />
                <span style={{ fontSize: 13, color: brand.text }}>{s.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </>
  );
}

function LayoutGlyph({ id, brand, active, size = 44 }) {
  const fg = active ? brand.accent : brand.textDim;
  const bg = active ? `${brand.accent}25` : brand.panel;
  const stroke = active ? brand.accent : brand.border;
  return (
    <div style={{
      width: size, height: size * 1.4, borderRadius: 8, border: `1px solid ${stroke}`,
      background: bg, padding: 4, display: 'flex', flexDirection: 'column', gap: 3, flexShrink: 0,
    }}>
      {id === 'D' && (<>
        <div style={{ flex: 1.4, background: fg, borderRadius: 3, opacity: .85 }}/>
        <div style={{ height: 3, background: fg, borderRadius: 1, opacity: .5, width: '60%' }}/>
        <div style={{ height: 3, background: fg, borderRadius: 1, opacity: .5 }}/>
        <div style={{ height: 3, background: fg, borderRadius: 1, opacity: .5, width: '80%' }}/>
      </>)}
      {id === 'B' && (<>
        <div style={{ height: 14, background: fg, borderRadius: 3, opacity: .8 }}/>
        <div style={{ flex: 1, background: fg, borderRadius: 3, opacity: .35 }}/>
        <div style={{ flex: 1, background: fg, borderRadius: 3, opacity: .35 }}/>
      </>)}
      {id === 'C' && (<>
        <div style={{ flex: 1, background: fg, borderRadius: 3, opacity: .35 }}/>
        <div style={{ display: 'flex', gap: 2, height: 8 }}>
          {[0,1,2,3,4].map(i => <div key={i} style={{ flex: 1, background: fg, borderRadius: 2, opacity: i === 0 ? .9 : .4 }}/>)}
        </div>
      </>)}
      {id === 'A' && (<>
        <div style={{ height: 6, background: fg, borderRadius: 1, opacity: .6, width: '50%' }}/>
        <div style={{ height: 3, background: fg, borderRadius: 1, opacity: .4, width: '80%' }}/>
        <div style={{ height: 3, background: fg, borderRadius: 1, opacity: .4 }}/>
        <div style={{ height: 3, background: fg, borderRadius: 1, opacity: .4 }}/>
        <div style={{ height: 3, background: fg, borderRadius: 1, opacity: .4, width: '70%' }}/>
        <div style={{ height: 3, background: fg, borderRadius: 1, opacity: .4 }}/>
      </>)}
    </div>
  );
}
